/**
 * RequestPriorityCatalog — Settings ▸ Request Priorities.
 *
 * The levels an RM ranks a Staffing Request with. The list order IS the ranking (top = most
 * urgent). Priorities are deactivated, never
 * deleted, so tags on historical requests stay legible.
 */

import { ArrowDown, ArrowUp, Flag, MoreHorizontal, Pencil } from "lucide-react";
import { useState } from "react";
import { RequestPriorityEditorDialog } from "@/components/admin/RequestPriorityEditorDialog";
import { DataTable } from "@/components/shared/data-table";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  type StaffingRequestPriorityView,
  useStaffingRequestPriorities,
  useUpdateStaffingRequestPriority,
} from "@/hooks/use-staffing-requests";

type DialogState =
  | { type: "none" }
  | { type: "create" }
  | { type: "edit"; requestPriority: StaffingRequestPriorityView };

export function RequestPriorityCatalog() {
  const {
    data: priorities = [],
    isLoading,
    isError,
    error,
    refetch,
  } = useStaffingRequestPriorities();
  const update = useUpdateStaffingRequestPriority();
  const [dialog, setDialog] = useState<DialogState>({ type: "none" });

  /** Swap two neighbours' sort order — the list order is the ranking. */
  function swapRank(from: number, to: number) {
    const a = priorities[from];
    const b = priorities[to];
    if (!a || !b) return;
    if (a.sortOrder === b.sortOrder) {
      // Tied (shouldn't happen via the UI): spread them so the swap actually moves.
      update.mutate({ id: a.id, sortOrder: b.sortOrder + (to < from ? -1 : 1) });
      return;
    }
    update.mutate({ id: a.id, sortOrder: b.sortOrder });
    update.mutate({ id: b.id, sortOrder: a.sortOrder });
  }

  if (isError) {
    return (
      <ErrorState
        message={error?.message ?? "Failed to load Request Priorities"}
        onRetry={refetch}
      />
    );
  }

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="font-display text-lg font-semibold">Request Priorities</h2>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            The levels a Resource Manager can rank a Staffing Request with — one per request. List
            the most urgent first; use Move up / Move down to reorder. Priorities are for context
            and filtering; they don't change how a request is mapped or committed.
          </p>
        </div>
        <Button size="sm" onClick={() => setDialog({ type: "create" })}>
          Add Request Priority
        </Button>
      </header>

      <DataTable
        headers={[
          { label: "Rank", className: "w-16" },
          { label: "Priority" },
          { label: "Requests", className: "text-right" },
          { label: "Status" },
          { label: "Actions", className: "text-right" },
        ]}
        loading={isLoading}
        empty={
          <EmptyState
            icon={Flag}
            title="No Request Priorities yet"
            description="Add the first priority to start ranking Staffing Requests."
          />
        }
      >
        {priorities.map((requestPriority, index) => (
          <tr
            key={requestPriority.id}
            className="border-b border-border last:border-0 transition-colors hover:bg-muted/30"
          >
            <td className="px-4 py-3 tabular-nums text-muted-foreground">{index + 1}</td>
            <td className="px-4 py-3">
              <div className="font-medium">{requestPriority.name}</div>
              {requestPriority.description && (
                <div className="text-xs text-muted-foreground">{requestPriority.description}</div>
              )}
            </td>
            <td className="px-4 py-3 text-right tabular-nums text-muted-foreground">
              {requestPriority.usageCount}
            </td>
            <td className="px-4 py-3">
              {requestPriority.active ? (
                <Badge variant="secondary">Active</Badge>
              ) : (
                <Badge variant="outline">Inactive</Badge>
              )}
            </td>
            <td className="px-4 py-3 text-right">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={`Actions for ${requestPriority.name}`}
                  >
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => setDialog({ type: "edit", requestPriority })}>
                    <Pencil className="h-4 w-4" />
                    Edit
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    disabled={index === 0 || update.isPending}
                    onClick={() => swapRank(index, index - 1)}
                  >
                    <ArrowUp className="h-4 w-4" />
                    Move up
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    disabled={index === priorities.length - 1 || update.isPending}
                    onClick={() => swapRank(index, index + 1)}
                  >
                    <ArrowDown className="h-4 w-4" />
                    Move down
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() =>
                      update.mutate({ id: requestPriority.id, active: !requestPriority.active })
                    }
                  >
                    {requestPriority.active ? "Deactivate" : "Reactivate"}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </td>
          </tr>
        ))}
      </DataTable>

      <p className="text-xs text-muted-foreground">
        Priorities are deactivated, never deleted. A deactivated priority disappears from the
        picker, but requests already set to it keep it.
      </p>

      {dialog.type !== "none" && (
        <RequestPriorityEditorDialog
          open
          onOpenChange={(open) => !open && setDialog({ type: "none" })}
          requestPriority={dialog.type === "edit" ? dialog.requestPriority : undefined}
        />
      )}
    </div>
  );
}
